import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import { Actions as ActionsIcon } from "@/components/icons";
import type { TableColumn } from "@/types/component";
import { toast } from "sonner";
import type { LibraryFolderItem } from "../types/library.types";
import { LibraryShareType } from "../types/library.types";
import { libraryApi, libraryScopeFromBasePath } from "../services/library.api";
import AddFolderModal from "../components/AddFolderModal";
import AddDocumentModal from "../components/AddDocumentModal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import type { LibraryBasePath } from "./LibraryPage";
import styles from "./FoldersPage.module.scss";

interface FoldersPageProps {
  basePath?: LibraryBasePath;
}

const FolderIconSmall: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 72 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M31.5 13.5H8.23544C4.92977 13.5 2.25 15.8113 2.25 19.125V29.25C2.25 25.9363 4.92977 23.25 8.23544 23.25L31.5 22.5L36 18L31.5 13.5Z"
      fill="url(#folderPaint0)"
    />
    <path
      d="M2.25 28.5C2.25 25.1863 5.12817 22.5 8.67857 22.5H31.5L36 18H63.3214C66.8718 18 69.75 20.6863 69.75 24V57.0001C69.75 60.3138 66.8718 63.0001 63.3214 63.0001H8.67857C5.12817 63.0001 2.25 60.3138 2.25 57.0001L2.25 28.5Z"
      fill="url(#folderPaint1)"
    />
    <defs>
      <linearGradient
        id="folderPaint0"
        x1="19.125"
        y1="11.25"
        x2="19.125"
        y2="29.25"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E6A200" />
        <stop offset="1" stopColor="#C68600" />
      </linearGradient>
      <linearGradient
        id="folderPaint1"
        x1="36"
        y1="15.75"
        x2="36"
        y2="63"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFD766" />
        <stop offset="1" stopColor="#FEC52C" />
      </linearGradient>
    </defs>
  </svg>
);

const FoldersPage: React.FC<FoldersPageProps> = ({
  basePath = "admin-library",
}) => {
  const prefix = `/dashboard/${basePath}`;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  // Kursiyer kütüphanesi (/dashboard/library) salt okunurdur; oluşturma uçları yalnız /api/admin altında.
  const canCreate =
    hasPermission(PERMISSIONS.LIBRARY_CREATE) && basePath !== "library";
  const [folders, setFolders] = useState<LibraryFolderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalFolders, setTotalFolders] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
  const [addFolderModalMode, setAddFolderModalMode] = useState<
    "create" | "add"
  >("create");
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await libraryApi.getItems({
        scope: libraryScopeFromBasePath(basePath),
        type: "folder",
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        updatedAfter: dateFilter || undefined,
      });
      const result = res as { data: LibraryFolderItem[]; total: number };
      setFolders(result.data);
      setTotalFolders(result.total);
    } finally {
      setLoading(false);
    }
  }, [basePath, currentPage, pageSize, searchTerm, dateFilter]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const handleSearch = (value: string) => setSearchTerm(value);
  const handleFilterChange = (filters: Record<string, unknown>) => {
    const dateVal = filters.updatedDate;
    setDateFilter(
      dateVal instanceof Date
        ? dateVal.toISOString()
        : (dateVal as string) || null,
    );
  };

  const openAddFolderModal = (mode: "create" | "add") => {
    setAddFolderModalMode(mode);
    setAddFolderModalOpen(true);
  };
  const handleAddFolderModalClose = () => setAddFolderModalOpen(false);
  const handleAddFolderSubmit = async (payload: {
    folderName: string;
    share: "everyone" | "admins" | "custom";
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
    uploadedFileIds?: string[];
    files?: File[];
  }) => {
    const shareType =
      payload.share === "everyone"
        ? LibraryShareType.EVERYONE
        : payload.share === "admins"
          ? LibraryShareType.ADMINS
          : LibraryShareType.PRIVATE;
    try {
      const { id: folderId } = await libraryApi.createFolder({
        name: payload.folderName,
        shareType,
        sharedUserIds:
          payload.share === "custom" ? payload.sharedUserIds : undefined,
        sharedDepartmentIds:
          payload.share === "custom" ? payload.sharedDepartmentIds : undefined,
      });
      const hasFiles =
        (payload.uploadedFileIds?.length ?? 0) > 0 ||
        (payload.files?.length ?? 0) > 0;
      if (hasFiles) {
        await libraryApi.uploadDocuments({
          fileIds: payload.uploadedFileIds,
          files: payload.files,
          parentItemId: folderId,
          shareType,
          sharedUserIds:
            payload.share === "custom" ? payload.sharedUserIds : undefined,
          sharedDepartmentIds:
            payload.share === "custom"
              ? payload.sharedDepartmentIds
              : undefined,
        });
        toast.success(
          t("library.folder.createdWithFiles"),
        );
      } else {
        toast.success(t("library.folder.created"));
      }
      handleAddFolderModalClose();
      fetchFolders();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    }
  };

  const handleAddDocumentModalClose = () => setAddDocumentModalOpen(false);
  const handleAddDocumentSubmit = async (payload: {
    share: "everyone" | "admins" | "custom";
    uploadedFileIds?: string[];
    files?: File[];
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
  }) => {
    const shareType =
      payload.share === "everyone"
        ? LibraryShareType.EVERYONE
        : payload.share === "admins"
          ? LibraryShareType.ADMINS
          : LibraryShareType.PRIVATE;
    try {
      await libraryApi.uploadDocuments({
        fileIds: payload.uploadedFileIds?.length
          ? payload.uploadedFileIds
          : undefined,
        files: payload.uploadedFileIds?.length ? undefined : payload.files,
        shareType,
        sharedUserIds:
          payload.share === "custom" ? payload.sharedUserIds : undefined,
        sharedDepartmentIds:
          payload.share === "custom" ? payload.sharedDepartmentIds : undefined,
      });
      toast.success(t("library.document.uploaded"));
      handleAddDocumentModalClose();
      fetchFolders();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    }
  };

  const handleFolderMenuAction = (
    action: string,
    folder: LibraryFolderItem,
  ) => {
    if (action === "open") {
      navigate(`${prefix}/folders/${folder.id}`, {
        state: { folderName: folder.name },
      });
    }
    // edit, delete TODO
  };

  const handleDropdownToggle = (id: string, open: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [id]: open }));
  };

  const dateFilterField = {
    name: "updatedDate",
    label: t("library.filters.updatedDate"),
    type: "date" as const,
    placeholder: t("library.filters.updatedDatePlaceholder"),
  };

  const folderColumns: TableColumn<LibraryFolderItem>[] = [
    {
      header: t("library.folders.cols.name"),
      accessor: (row) => (
        <div className={styles.folderNameCell}>
          <FolderIconSmall className={styles.folderIcon} />
          <span>{row.name}</span>
        </div>
      ),
    },
    {
      header: t("library.folders.cols.uploadedBy"),
      accessor: (row) => row.uploadedBy ?? "—",
    },
    {
      header: t("library.folders.cols.lastUpdated"),
      accessor: (row) => row.lastUpdatedFormatted,
    },
    {
      header: t("library.folders.cols.fileSize"),
      accessor: () => "—",
    },
    {
      header: t("library.folders.cols.sharedWith"),
      accessor: () => (
        <div className={styles.sharedWith}>
          <div className={styles.avatarStack}>
            {[1, 2, 3].map((i) => (
              <div key={i} className={styles.avatarCircle} title={`User ${i}`}>
                {i}
              </div>
            ))}
          </div>
          <span className={styles.plusBadge}>—</span>
        </div>
      ),
    },
    {
      header: t("library.documents.cols.action"),
      accessor: (row) => {
        const isOpen = openDropdowns[row.id] ?? false;
        return (
          <Popover
            content={
              <div className={styles.menuDropdown}>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => handleFolderMenuAction("open", row)}
                >
                  {t("library.folder.open")}
                </button>
                {basePath !== "library" && (
                  <>
                    <button
                      type="button"
                      className={styles.menuItem}
                      onClick={() => handleFolderMenuAction("edit", row)}
                    >
                      {t("library.folder.edit")}
                    </button>
                    <button
                      type="button"
                      className={styles.menuItem}
                      onClick={() => handleFolderMenuAction("delete", row)}
                    >
                      {t("library.folder.delete")}
                    </button>
                  </>
                )}
              </div>
            }
            isOpen={isOpen}
            onClose={() => handleDropdownToggle(row.id, false)}
            position="bottom"
          >
            <button
              type="button"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownToggle(row.id, !isOpen);
              }}
            >
              <ActionsIcon width={20} height={20} />
            </button>
          </Popover>
        );
      },
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("library.title")} />

      <div className={styles.pageContainer}>
        <div className={styles.breadcrumb}>
          <button
            type="button"
            className={styles.breadcrumbLink}
            onClick={() => navigate(prefix)}
          >
            {t("library.title")}
          </button>
          <span className={styles.breadcrumbSep}>&gt;</span>
          <span className={styles.breadcrumbCurrent}>
            {t("library.folders.title")}
          </span>
        </div>

        <PageHeader
          searchPlaceholder={t("library.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={handleSearch}
          filters={[dateFilterField]}
          onFilterChange={handleFilterChange}
          addButtonLabel={
            canCreate ? t("library.addButton") : undefined
          }
          addMenuOptions={
            canCreate
              ? [
                  {
                    label: t("library.addMenu.createFolder"),
                    onClick: () => openAddFolderModal("create"),
                  },
                  {
                    label: t("library.addMenu.addFolder"),
                    onClick: () => openAddFolderModal("add"),
                  },
                  {
                    label: t("library.addMenu.addDocument"),
                    onClick: () => setAddDocumentModalOpen(true),
                  },
                ]
              : undefined
          }
          viewMode="list"
          onViewModeChange={() => {}}
        />

        <div className={styles.tableWrap}>
          <Table
            columns={folderColumns}
            data={folders}
            isLoading={loading}
            getRowId={(row) => row.id}
            totalRecords={totalFolders}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={(p) => setCurrentPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setCurrentPage(1);
            }}
            onRowClick={(row) =>
              navigate(`${prefix}/folders/${row.id}`, {
                state: { folderName: row.name },
              })
            }
            renderActions={() => null}
            hasVisibleActions={false}
            openDropdowns={openDropdowns}
            contentBasedHeight
            emptyMessage={t("library.folders.noData")}
          />
        </div>

        <AddFolderModal
          isOpen={addFolderModalOpen}
          onClose={handleAddFolderModalClose}
          mode={addFolderModalMode}
          onSubmit={handleAddFolderSubmit}
        />

        <AddDocumentModal
          isOpen={addDocumentModalOpen}
          onClose={handleAddDocumentModalClose}
          onSubmit={handleAddDocumentSubmit}
        />
      </div>
    </DashboardLayout>
  );
};

export default FoldersPage;
